import { FC, useCallback, useEffect, useState } from 'react';
import {
  Stack,
  TextInput,
  ColorInput,
  Select,
  Button,
  Group,
  Box,
  Collapse,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconPlus, IconCheck, IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { PedigreeCondition } from '../types';

// -----------------------------------------------------------------------------
const allColors = [
  '#7f8496',
  '#a17f74',
  '#f06e27',
  '#fc931d',
  '#ffba38',
  '#dbd33e',
  '#87de58',
  '#3bdf70',
  '#99e2ff',
  '#3aaefe',
  '#5474b4',
  '#0058e4',
  '#1f32c4',
  '#424e88',
  '#7c55de',
  '#504c97',
  '#9354e0',
  '#a34bcb',
  '#c337e0',
  '#f243eb',
  '#f24391',
  '#f02866',
  '#e22862',
  '#e3353f',
  '#f76d6d',
];

// -----------------------------------------------------------------------------
interface ConditionFormProps {
  onAddCondition: (condition: Omit<PedigreeCondition, 'id'>) => void;
  existingConditions?: PedigreeCondition[];
  buttonLabel?: string;
  buttonVariant?: 'filled' | 'outline' | 'subtle';
  buttonSize?: 'xs' | 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  onUnsavedStateChange?: (hasUnsavedChanges: boolean) => void;
}

// -----------------------------------------------------------------------------
export const ConditionForm: FC<ConditionFormProps> = ({
  onAddCondition,
  existingConditions = [],
  buttonLabel = 'Add Condition',
  buttonVariant = 'filled',
  buttonSize = 'sm',
  fullWidth = false,
  onUnsavedStateChange,
}) => {
  const { t } = useTranslation('pedigree');
  const [isFormOpen, { toggle: toggleForm, close: closeForm }] =
    useDisclosure(false);
  const [newConditionName, setNewConditionName] = useState('');

  const [newConditionColor, setNewConditionColor] = useState('');
  const [newConditionSymbol, setNewConditionSymbol] = useState('');
  const [newConditionPattern, setNewConditionPattern] = useState<
    'solid' | 'horizontal' | 'vertical' | 'diagonal'
  >('solid');

  const [availableColors, setAvailableColors] = useState<string[]>([]);

  // ---------------------------------------------------------------------------
  const setInitialColor = useCallback(() => {
    const usedColors = existingConditions.map((condition) => condition.color);
    const newAvailableColors = allColors.filter(
      (color) => !usedColors.includes(color)
    );

    setNewConditionColor(newAvailableColors[0] || '#cccccc');
    setAvailableColors(newAvailableColors);
  }, [existingConditions]);

  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!isFormOpen) {
      return;
    }

    setInitialColor();
  }, [setInitialColor, isFormOpen]);

  // ---------------------------------------------------------------------------
  // Track unsaved changes and notify parent component
  useEffect(() => {
    const hasUnsavedChanges = isFormOpen && newConditionName.trim() !== '';
    onUnsavedStateChange?.(hasUnsavedChanges);
  }, [isFormOpen, newConditionName, onUnsavedStateChange]);

  // ---------------------------------------------------------------------------
  const resetForm = () => {
    setNewConditionName('');
    setInitialColor();
    setNewConditionSymbol('');
    setNewConditionPattern('solid');
  };

  // ---------------------------------------------------------------------------
  const handleAddCondition = () => {
    if (newConditionName.trim()) {
      onAddCondition({
        name: newConditionName.trim(),
        color: newConditionColor,
        symbol: newConditionSymbol.trim() || undefined,
        pattern: newConditionPattern,
      });

      resetForm();
      closeForm();
    }
  };

  // ---------------------------------------------------------------------------
  const handleCancel = () => {
    resetForm();
    closeForm();
  };

  // ---------------------------------------------------------------------------
  return (
    <Box>
      <Button
        size={buttonSize}
        variant={buttonVariant}
        leftSection={<IconPlus size={buttonSize === 'xs' ? 12 : 16} />}
        onClick={toggleForm}
        fullWidth={fullWidth}
      >
        {buttonLabel}
      </Button>

      {/* Add Condition Form */}
      <Collapse in={isFormOpen}>
        <Box className="rounded-[4px]" mt="xs" p="xs" bg="gray.0">
          <Stack gap="xs">
            {' '}
            <TextInput
              size="xs"
              placeholder={t('conditionForm.namePlaceholder')}
              value={newConditionName}
              onChange={(e) => setNewConditionName(e.currentTarget.value)}
              label={t('conditionForm.nameLabel')}
              required
            />
            <TextInput
              size="xs"
              placeholder={t('conditionForm.symbolPlaceholder')}
              value={newConditionSymbol}
              onChange={(e) => setNewConditionSymbol(e.currentTarget.value)}
              label={t('conditionForm.symbolLabel')}
              description={t('conditionForm.symbolDescription')}
            />
            <ColorInput
              size="xs"
              value={newConditionColor}
              onChange={setNewConditionColor}
              label={t('conditionForm.colorLabel')}
              format="hex"
              withPicker={false}
              withEyeDropper={false}
              swatches={availableColors}
              swatchesPerRow={5}
              description={
                availableColors.length === 0
                  ? t('conditionForm.noMoreColors')
                  : undefined
              }
              disabled={availableColors.length === 0}
            />
            <Select
              size="xs"
              value={newConditionPattern}
              onChange={(value) =>
                setNewConditionPattern(
                  value as 'solid' | 'horizontal' | 'vertical' | 'diagonal'
                )
              }
              label={t('conditionForm.patternLabel')}
              data={[
                { value: 'solid', label: t('conditionForm.patterns.solid') },
                {
                  value: 'horizontal',
                  label: t('conditionForm.patterns.horizontal'),
                },
                {
                  value: 'vertical',
                  label: t('conditionForm.patterns.vertical'),
                },
                {
                  value: 'diagonal',
                  label: t('conditionForm.patterns.diagonal'),
                },
              ]}
            />
            <Group gap="xs" justify="flex-end" mt="xs">
              {' '}
              <Button
                size="xs"
                variant="subtle"
                leftSection={<IconX size={12} />}
                onClick={handleCancel}
              >
                {t('conditionForm.cancel')}
              </Button>{' '}
              <Button
                size="xs"
                variant="filled"
                leftSection={<IconCheck size={12} />}
                onClick={handleAddCondition}
                disabled={
                  !newConditionName.trim() || availableColors.length === 0
                }
              >
                {t('conditionForm.add')}
              </Button>
            </Group>
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
};
